import React, { useMemo, useRef, useState } from 'react'
import KanBoIcon from 'components/KanBoIcon'
import User from 'components/ControlledUser'
import BusyOverlay from 'components/BusyOverlay'
import CardUser from './components/CardUser'
import Scrollbars from 'components/Scrollbars'
import classes from './Card.module.scss'
import moment from 'moment'
import { CardLink } from 'components/Link'
import {
  ToggleModal
} from 'components/Modal'
import * as CardStatusRoles from 'const/CardStatusRoles'
import { FormattedMessage } from 'react-intl'
import styled, { css } from 'styled-components'
import useInterval from 'hooks/useInterval'
import { useColor } from 'utils/colors'
import { getStatusIconName } from 'utils/status'
import { useUncontrolled } from 'uncontrollable'
import useDidUpdate from 'hooks/useDidUpdate'
import {
  MenuItem
} from 'components/Menu'
import {
  ModalMenu
} from 'components/Modal'
import archivedImage from './archived_bg.png'

export const dragType = 'CARD'

const StyledBusyOverlay = styled(BusyOverlay)`
  inset: 0;
  border-radius: 5px;
`

const DraggingOverlay = styled.span`
  position: absolute;
  display: block;
  inset: 0;
  z-index: 2;
  background-color: #ccc;
  border-radius: 5px;
`

const DraggableOverOverlay = styled(DraggingOverlay)`
  opacity: 0.5;
  text-align: center;
  vertical-align: middle;
`

const TemplateLabel = styled.div`
  & > :first-child {
    font-size: 16px;
    margin-right: .25em;
  }
`

const CardBody = styled.span`
  width: 100%;
  min-height: 40px;
  display: flex;
  align-items: flex-start;
  word-wrap: break-word;
  padding: 5px;
`

const Name = styled.span`
  width: 100%;
  word-wrap: break-word;
  overflow: hidden;
  margin-right: 5px;
`

const ResponsibleContainer = styled.span`
  position: relative;
  display: block;
`

const RightPart = styled.span`
  display: flex;
  flex: 0 0 auto;
  margin-left: auto;
`

export const CardValue = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  border-radius: 24px;
  padding: 2px 4px;
  margin-right: 2px;
  margin-top: 2px;
  color: #777;
  background-color: rgba(0,0,0,0.04);

  ${props => props.onClick ? css`
    cursor: pointer;

    &:hover {
      background-color: rgba(0,0,0,0.1);
    }
  ` : undefined}

  ${props => props.isSelected ? css`
    color: #2196f3;
  ` : undefined}
`

const Status = styled(CardValue)`
  flex: 0 0 auto;
  margin: 0;
  width: 30px;
  height: 30px;
  background-color: transparent;
`

const Menu = styled(CardValue)`
  flex: 0 0 auto;
  margin: 0;
  width: 30px;
  height: 30px;
  background-color: transparent;
`

const UnreadValue = styled(CardValue)`
  color: #fb8c00;
`

const SubCards = styled(Scrollbars)`
  display: block;
  padding: 10px;
  margin: 10px;
  margin-top: 0;
  background-color: #eceff1;
  border-radius: 5px;
  box-shadow: inset 0 0 3px 0px rgba(0, 0, 0, 0.3);

  & > * {
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }
`

const CardRoot = styled(CardLink)`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  background-color: #fff;
  min-height: 40px;
  border-radius: 5px;
  box-shadow: 0 1px 3px 0 rgba(0,0,0,0.3);

  ${props => props.hover ? css`
    cursor: pointer;

    &:hover {
      background-color: #fafafa;
    }
  ` : undefined}

  ${props => props.isArchived ? css`
    background-image: url(${archivedImage});
  ` : undefined}

  ${props => props.inline ? css`
    ${Name} {
      width: auto;
    }

    ${RightPart} {
      margin-left: initial;
    }
  ` : undefined}
`

const Label = ({ name, color }) => {
  const tagColor = useColor(color)

  return (
    <CardValue
      style={{ color: tagColor }}
    >
      <KanBoIcon
        fixedWidth
        className={classes.iconMargin}
        name='kanbo-16-label'
        style={{ fontSize: 16 }}
      />
      {name}
    </CardValue>
  )
}

const Card = React.forwardRef<any, any>((props, ref) => {
  const {
    id,
    name,
    responsible,
    users,
    labels,
    startDate,
    dueDate,
    status,
    isObserved,
    isArchived,
    isTemplate,
    isBoardClosed,
    unreadCount,
    checklistItemCount,
    completedChecklistItemCount,
    subcards,
    completedSubcardCount,
    isBusy,
    issueLevel,
    noAccess,
  
    userComponent = User,
    hover,
    className,
    style,
    linkName,
    footer,
    isDragging,
    isDraggableOver,
    inline,
    values,
    onClick,
    onStatusClick,
    showChildren = false,
    onShowChildren,
    menu,
    showMenu = false,
    onMenuToggle,

    ...restProps
  } = useUncontrolled(props, {
    showChildren: 'onShowChildren',
    showMenu: 'onMenuToggle',
  })

  useDidUpdate(() => {
    onShowChildren(props.defaultShowChildren)
  }, [props.defaultShowChildren])

  const isTouchedRef = useRef(false)

  const [nowMoment, setNowMoment] = useState(() => moment())

  useInterval(() => {
    setNowMoment(moment())
  }, 60000)

  const [
    isStartDateOverdue,
    isStartDateSoonOverdue,
    isDueDateOverdue,
    isDueDateSoonOverdue,
  ] = useMemo(() => {
    let isStartDateOverdue = false
    let isStartDateSoonOverdue = false

    let isDueDateOverdue = false
    let isDueDateSoonOverdue = false

    if (!isArchived && !isTemplate && !isBoardClosed && status) {
      if (
        startDate &&
        status.role === CardStatusRoles.NOT_STARTED
      ) {
        if (moment(startDate).isSameOrBefore(nowMoment)) {
          isStartDateOverdue = true
        } else if (moment(startDate).diff(nowMoment, 'days') <= 7) {
          isStartDateSoonOverdue = true
        }
      }

      if (
        dueDate &&
        status.role !== CardStatusRoles.NONE &&
        status.role !== CardStatusRoles.COMPLETED
      ) {
        if (moment(dueDate).isSameOrBefore(nowMoment)) {
          isDueDateOverdue = true
        } else if (moment(dueDate).diff(nowMoment, 'days') <= 7) {
          isDueDateSoonOverdue = true
        }
      }
    }

    return [
      isStartDateOverdue,
      isStartDateSoonOverdue,
      isDueDateOverdue,
      isDueDateSoonOverdue,
    ]
  }, [nowMoment, isArchived, isTemplate, isBoardClosed, startDate, dueDate, status])

  const statusColor = useColor(status?.color)

  const preventDefaultClick = (event) => {
    event.stopPropagation()
    event.preventDefault()
  }

  const handleTouchStart = () => {
    isTouchedRef.current = true
  }

  const handleTouchEnd = () => {
    isTouchedRef.current = false
  }

  const handleContextMenu = (event) => {
    if (isTouchedRef.current) {
      event.preventDefault()

      return false
    }
  }

  const renderMenu = () => {
    return !!menu && (
      <ModalMenu
        show={showMenu}
        onToggle={onMenuToggle}
      >
        <Menu
          onClick={preventDefaultClick}
        >
          <KanBoIcon
            fixedWidth
            name='kanbo-16-options'
            style={{ fontSize: 16 }}
          />
        </Menu>
        {Array.isArray(menu)
          ? menu.map((menuItem, index) => {
            if (React.isValidElement(menuItem)) {
              return menuItem
            }

            const { title, ...menuItemProps } = menuItem

            return (
              <MenuItem
                {...menuItemProps}
                key={index}
              >
                {title}
              </MenuItem>
            )
          })
          : menu
        }
      </ModalMenu>
    )
  }

  let startDateClassName = classes.dateIcon
  if (isStartDateOverdue) {
    startDateClassName += ' text-danger'
  } else if (isStartDateSoonOverdue) {
    startDateClassName += ' text-warning'
  }

  let dueDateClassName = classes.dateIcon
  if (isDueDateOverdue) {
    dueDateClassName += ' text-danger'
  } else if (isDueDateSoonOverdue) {
    dueDateClassName += ' text-warning'
  }

  return (
    <CardRoot
      {...restProps}
      ref={ref}
      cardId={id}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onContextMenu={handleContextMenu}
      className={`clear-link-style${className ? ` ${className}` : ''}`}
      style={style}
      hover={hover || linkName || onClick}
      inline={inline}
      isArchived={isArchived}
      onClick={onClick}
    >
      {isBusy && <StyledBusyOverlay />}
      {isDragging && <DraggingOverlay />}
      {isDraggableOver && <DraggableOverOverlay />}
      <CardBody>
        {status &&
          <Status
            style={{ color: statusColor }}
            title={status.name}
            onClick={onStatusClick ? (event) => {
              event.stopPropagation()
              event.preventDefault()

              onStatusClick(event)
            } : undefined}
          >
            <KanBoIcon
              fixedWidth
              name={getStatusIconName(status.role)}
              style={{ fontSize: 16 }}
            />
          </Status>
        }
        <span className={classes.content}>
          {noAccess ? (
            <Name className='text-danger'>
              <FormattedMessage id='CARD_FRONT_ACCESS_DENIED' />
            </Name>
          ) : (
            <Name>
              {name}
              {footer &&
                <span className={classes.footer}>{footer}</span>
              }
            </Name>
          )}
          {values}
          {issueLevel &&
            <CardValue
              style={{
                fontSize: 16,
                color: '#fff',
                ...(() => {
                  switch (issueLevel) {
                    case 'danger':
                      return {
                        backgroundColor: '#f55a4e'
                      }
                    case 'warning':
                      return {
                        backgroundColor: '#fb8c00'
                      }
                    default:
                      return {}
                  }
                })()
              }}
            >
              <KanBoIcon
                fixedWidth
                name='kanbo-16-exclamation'
                style={{ fontSize: 16 }}
              />
            </CardValue>
          }
          {labels && labels.map(label =>
            <Label
              key={label.id}
              name={label.name}
              color={label.color}
            />
          )}
          {isTemplate &&
            <TemplateLabel className='text-primary'>
              <KanBoIcon fixedWidth name='kanbo-16-template' />
              <FormattedMessage id='CARD_TEMPLATE_LABEL' />
            </TemplateLabel>
          }
          {startDate &&
            <FormattedMessage
              id='DATE_FORMAT_FULL'
              values={{
                date: moment(startDate).format('LLLL'),
                week: moment(startDate).format('w'),
              }}
            >
              {formattedMessage =>
                <CardValue
                  title={formattedMessage as string}
                >
                  <span className={startDateClassName}>
                    <KanBoIcon
                      fixedWidth
                      className={classes.iconMargin}
                      name='kanbo-16-start-date'
                      style={{ fontSize: 16 }}
                    />
                    {moment(startDate).format('D MMM')}
                  </span>
                </CardValue>
              }
            </FormattedMessage>
          }
          {dueDate &&
            <FormattedMessage
              id='DATE_FORMAT_FULL'
              values={{
                date: moment(dueDate).format('LLLL'),
                week: moment(dueDate).format('w'),
              }}
            >
              {formattedMessage =>
                <CardValue
                  title={formattedMessage as string}
                >
                  <span className={dueDateClassName}>
                    <KanBoIcon
                      fixedWidth
                      className={classes.iconMargin}
                      name='kanbo-16-due-date'
                      style={{ fontSize: 16 }}
                    />
                    {moment(dueDate).format('D MMM')}
                  </span>
                </CardValue>
              }
            </FormattedMessage>
          }
          {subcards?.length > 0 &&
            <CardValue
              isSelected={showChildren}
              onClick={(event) => {
                event.stopPropagation()
                event.preventDefault()

                onShowChildren(!showChildren)
              }}
            >
              <KanBoIcon
                fixedWidth
                className={classes.iconMargin}
                name='kanbo-16-relation-child'
                style={{ fontSize: 16 }}
              />
              {completedSubcardCount}{'/'}{subcards.length}
            </CardValue>
          }
          {checklistItemCount > 0 &&
            <CardValue>
              <KanBoIcon
                fixedWidth
                className={classes.iconMargin}
                name='kanbo-16-tasks'
                style={{ fontSize: 16 }}
              />
              {completedChecklistItemCount}{'/'}{checklistItemCount}
            </CardValue>
          }
          {isObserved &&
            <CardValue>
              <KanBoIcon
                fixedWidth
                name='kanbo-16-follow'
                style={{ fontSize: 16 }}
              />
            </CardValue>
          }
          {unreadCount > 0 &&
            <UnreadValue>
              <KanBoIcon
                className={classes.iconMargin}
                name='kanbo-16-solid-notifications'
                style={{ fontSize: 16 }}
              />
              {unreadCount}
            </UnreadValue>
          }
        </span>
        {(responsible || !!menu) &&
          <RightPart>
            {responsible &&
              <ResponsibleContainer>
                <CardUser
                  userComponent={userComponent}
                  isResponsible
                  avatar
                  defaultMenu
                  presence
                  id={responsible}
                  size={30}
                />
                {users.length > 0 &&
                  <ToggleModal
                    attachment='top left'
                    targetAttachment='top right'
                    constraints={[
                      {
                        to: 'window',
                        pin: true
                      }
                    ]}
                  >
                    <span
                      className={classes.usersCount}
                      onClick={preventDefaultClick}
                    >
                      {users.length < 99 ? users.length : 99}
                    </span>
                    <Scrollbars
                      className={classes.modal + ' modal-content'}
                    >
                      {users.map(userId =>
                        <CardUser
                          userComponent={userComponent}
                          key={userId}
                          listItem
                          defaultMenu
                          presence
                          id={userId}
                        />
                      )}
                    </Scrollbars>
                  </ToggleModal>
                }
              </ResponsibleContainer>
            }
            {renderMenu()}
          </RightPart>
        }
      </CardBody>
      {(subcards?.length > 0 && showChildren) &&
        <SubCards>
          {subcards}
        </SubCards>
      }
    </CardRoot>
  )
})

export default Card
